"use client";

import { useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import {
  type Dispatch,
  type ReactElement,
  type SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { AddressSuggestion } from "@/features/geo/shared/address-types";
import { findPostalCode } from "@/features/geo/shared/postal-codes";
import { parseLeadResponse, taskPagePath } from "@/features/leads/shared/response";
import { buildLeadPayload } from "@/features/quote/shared/build-lead-payload";
import {
  type InitialFields,
  initialState,
  normalize,
  type State,
} from "@/features/quote/shared/quote-config";
import { EMAIL_RE } from "@/features/quote/shared/validation";
import {
  initialStepFor,
  phaseLabelFor,
  STEP_META,
  type StepId,
  wizardSteps,
} from "@/features/quote/shared/wizard-steps";
import { holdMatchingOverlay, MatchingOverlay } from "@/features/quote/ui/MatchingOverlay";
import { PrefillNotice } from "@/features/quote/ui/PrefillNotice";
import { ProgressBar, wizardPct } from "@/features/quote/ui/ProgressBar";
import { AddressStep } from "@/features/quote/ui/steps/AddressStep";
import { AudienceStep } from "@/features/quote/ui/steps/AudienceStep";
import { BusinessStep } from "@/features/quote/ui/steps/BusinessStep";
import { CallTimeStep } from "@/features/quote/ui/steps/CallTimeStep";
import { ContactStep } from "@/features/quote/ui/steps/ContactStep";
import { FrequencyStep } from "@/features/quote/ui/steps/FrequencyStep";
import { PostalStep } from "@/features/quote/ui/steps/PostalStep";
import { PreferencesStep } from "@/features/quote/ui/steps/PreferencesStep";
import { PriorityStep } from "@/features/quote/ui/steps/PriorityStep";
import { PropertyStep } from "@/features/quote/ui/steps/PropertyStep";
import { SqmStep } from "@/features/quote/ui/steps/SqmStep";
import { StartStep } from "@/features/quote/ui/steps/StartStep";
import { SubtypeStep } from "@/features/quote/ui/steps/SubtypeStep";
import { TaskStep } from "@/features/quote/ui/steps/TaskStep";
import { TimingStep } from "@/features/quote/ui/steps/TimingStep";
import { useWizardHeightLock } from "@/features/quote/ui/useWizardHeightLock";
import { WizardSidebar } from "@/features/quote/ui/WizardSidebar";
import { WizardStepShell } from "@/features/quote/ui/WizardStepShell";
import { type ListField, WizardProvider } from "@/features/quote/ui/wizard-context";
import type { BbrEnrichedData } from "@/integrations/bbr-codes";
import { generateEventId, trackEvent } from "@/shared/analytics/meta-pixel";
import { useUtmParams } from "@/shared/analytics/useUtmParams";
import { isValidDanishNational } from "@/shared/lib/phone";

function formatAddressLine(address: AddressSuggestion): string {
  const a = address.adresse;
  const unit = [a.etage ? `${a.etage}.` : null, a.dør].filter(Boolean).join(" ");
  const street = `${a.vejnavn} ${a.husnr}${unit ? `, ${unit}` : ""}`;
  return `${street}, ${a.postnr} ${a.postnrnavn}`;
}

// Floor for the hero card's step area (px) so short choice steps don't look
// cramped. Taller steps (address/contact) drive their own height, animated
// smoothly by the height lock.
const HERO_STEP_MIN_H = 460;

// Each step id maps to its own component; components read state and handlers
// via WizardProvider rather than props.
const STEP_COMPONENTS: Record<StepId, () => ReactElement> = {
  audience: AudienceStep,
  business: BusinessStep,
  task: TaskStep,
  subtype: SubtypeStep,
  address: AddressStep,
  property: PropertyStep,
  sqm: SqmStep,
  frequency: FrequencyStep,
  start: StartStep,
  timing: TimingStep,
  preferences: PreferencesStep,
  priority: PriorityStep,
  postal: PostalStep,
  callTime: CallTimeStep,
  contact: ContactStep,
};

export function QuoteWizard({
  initial = {},
  variant = "default",
  addressLookupEnabled = false,
  isRepeatTask = false,
}: {
  initial?: Partial<InitialFields>;
  variant?: "default" | "hero";
  // Arrived from the "Har du en opgave mere?" CTA (?igen=1). Gated on its own
  // flag rather than "any prefill param is present": marketing and price-
  // calculator deep-links also prefill, but for them a notice about "din
  // forrige opgave" would be wrong.
  isRepeatTask?: boolean;
  // Server-side flag mirroring isAddressLookupEnabled() (DATAFORDELER_API_KEY).
  // When false, the wizard skips the address step and falls back to the
  // postal-code-only flow - we never hit /api/address/* without a key.
  addressLookupEnabled?: boolean;
}) {
  const isHero = variant === "hero";
  const initialAudience = initial.audience ?? null;
  const audienceLocked = isHero && initialAudience !== null;
  const posthog = usePostHog();
  const router = useRouter();
  const utmParams = useUtmParams();
  const [state, setRawState] = useState<State>(() => initialState(initial));
  // Every write is normalized, so a step sets its own answer and dependent
  // answers (floor, frequency, subtype, date) clear themselves.
  const setState: Dispatch<SetStateAction<State>> = (action) =>
    setRawState((prev) => normalize(typeof action === "function" ? action(prev) : action));
  // A prepared link opens on the first unanswered step. The repeat-task flow
  // stays on step 1 so PrefillNotice can explain what it carried over.
  const [step, setStep] = useState(() =>
    isRepeatTask ? 1 : initialStepFor(state, addressLookupEnabled, audienceLocked),
  );
  const [direction, setDirection] = useState<1 | -1>(1);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Height lock (grow-only + hero floor) - see useWizardHeightLock. Keeps the
  // card from jumping when steps with different content heights switch.
  const {
    rootRef,
    stepsRef,
    lockStyle,
    scrollIntoView: scrollWizardIntoView,
  } = useWizardHeightLock({ floor: isHero ? HERO_STEP_MIN_H : 0 });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [honeypot, setHoneypot] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [matchFound, setMatchFound] = useState(false);
  const [bbrLoading, setBbrLoading] = useState(false);
  const [bbrError, setBbrError] = useState<string | null>(null);
  // Synchronous gate against double-clicks: `submitting` is state and only
  // takes effect on the next render. The server is still the source of truth.
  const inFlight = useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: fire once on mount
  useEffect(() => {
    posthog?.capture("wizard_started", {
      wizard: "tilbud",
      audience: initialAudience,
      // Lets us measure how many second tasks the thank-you CTA actually produces.
      is_repeat_task: isRepeatTask,
      // >1 means a deep-link pre-answered steps — how far it got the customer.
      initial_step: step,
    });
  }, []);

  function update<K extends keyof State>(key: K, value: State[K]) {
    setState((prev) => ({ ...prev, [key]: value }));
  }

  function setFieldError(field: string, error: string | null) {
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (error) next[field] = error;
      else delete next[field];
      return next;
    });
  }

  function validateEmailField(value: string): string | null {
    const v = value.trim();
    if (!v) return null;
    if (!EMAIL_RE.test(v)) return "Indtast en gyldig emailadresse";
    return null;
  }

  function validatePhoneField(value: string): string | null {
    const v = value.trim();
    if (!v) return null;
    if (!isValidDanishNational(v)) return "Indtast et gyldigt dansk telefonnummer (8 cifre)";
    return null;
  }

  function toggle<K extends ListField>(field: K, value: State[K][number]) {
    setState((prev) => {
      const list: State[K][number][] = prev[field];
      return {
        ...prev,
        [field]: list.includes(value) ? list.filter((v) => v !== value) : [...list, value],
      };
    });
  }

  async function handleAddressSelect(address: AddressSuggestion) {
    setBbrLoading(true);
    setBbrError(null);
    // Optimistic: set postalCode/streetAddress right away so the Next button
    // is active even if the BBR lookup takes a second.
    const fullText = formatAddressLine(address);
    setState((prev) => ({
      ...prev,
      address,
      addressSkipped: false,
      streetAddress: fullText,
      postalCode: address.adresse.postnr || prev.postalCode,
    }));

    try {
      const sp = new URLSearchParams({
        id: address.adresse.id,
        vejnavn: address.adresse.vejnavn,
        husnr: address.adresse.husnr,
        postnr: address.adresse.postnr,
        postnrnavn: address.adresse.postnrnavn,
        kommunekode: address.adresse.kommunekode,
      });
      if (address.adresse.etage) sp.set("etage", address.adresse.etage);
      if (address.adresse.dør) sp.set("doer", address.adresse.dør);
      const res = await fetch(`/api/address/lookup?${sp.toString()}`);
      if (!res.ok) {
        setBbrError("Vi kunne ikke hente boligoplysninger - udfyld manuelt nedenfor.");
        return;
      }
      const data = (await res.json()) as BbrEnrichedData;
      setState((prev) => {
        const isPrivatAudience = prev.audience === "privat";
        const isBusinessAudience = prev.audience === "erhverv";
        const sqmBbr = isPrivatAudience ? data.privat.squareMeters : data.erhverv.squareMeters;
        const next: State = {
          ...prev,
          streetAddress: data.display.fullText,
          postalCode: data.display.postnr || prev.postalCode,
          bbrPrefilled: { ...prev.bbrPrefilled },
        };
        if (isPrivatAudience) {
          if (data.privat.propertyType && prev.propertyType === "") {
            next.propertyType = data.privat.propertyType;
            next.bbrPrefilled.propertyType = true;
          }
          if (data.privat.floor && prev.floor === "") {
            next.floor = data.privat.floor;
            next.bbrPrefilled.floor = true;
          }
        }
        if (isBusinessAudience && data.erhverv.business && prev.business === null) {
          next.business = data.erhverv.business;
          next.bbrPrefilled.business = true;
        }
        if (sqmBbr && prev.squareMeters === "") {
          next.squareMeters = String(sqmBbr);
          next.bbrPrefilled.sqm = true;
        }
        return next;
      });
      posthog?.capture("bbr_lookup", {
        adgangsadresse_id: data.adgangsadresseId,
        has_property_type: Boolean(data.privat.propertyType),
        has_business: Boolean(data.erhverv.business),
        has_sqm: Boolean(data.privat.squareMeters || data.erhverv.squareMeters),
      });
    } catch (err) {
      console.error("BBR-opslag fejlede:", err);
      setBbrError("Vi kunne ikke hente boligoplysninger - udfyld manuelt nedenfor.");
    } finally {
      setBbrLoading(false);
    }
  }

  function handleSkipAddress() {
    setState((prev) => ({
      ...prev,
      address: null,
      addressSkipped: true,
      streetAddress: "",
      bbrPrefilled: { propertyType: false, floor: false, sqm: false, business: false },
    }));
    setBbrError(null);
    posthog?.capture("address_skipped", { audience: state.audience });
    autoAdvance(0);
  }

  function handleResetAddress() {
    setState((prev) => ({
      ...prev,
      address: null,
      addressSkipped: false,
      streetAddress: "",
      bbrPrefilled: { propertyType: false, floor: false, sqm: false, business: false },
    }));
    setBbrError(null);
  }

  const isPrivat = state.audience === "privat";
  const isBusiness = state.audience === "erhverv";
  const steps = wizardSteps(state, addressLookupEnabled, audienceLocked);
  const activeStepId: StepId | null = steps[step - 1] ?? null;
  const totalSteps = steps.length;

  const stepReady = (id: StepId) => STEP_META[id].ready(state);

  const postalMatch = useMemo(() => findPostalCode(state.postalCode), [state.postalCode]);

  function goToStep(n: number) {
    posthog?.capture("wizard_step_completed", {
      wizard: "tilbud",
      from_step: step,
      to_step: n,
      from_step_id: activeStepId,
      to_step_id: steps[n - 1] ?? null,
      audience: state.audience,
    });
    setDirection(n >= step ? 1 : -1);
    setStep(n);
    scrollWizardIntoView();
  }

  function autoAdvance(delay = 250) {
    const target = step + 1;
    setTimeout(() => goToStep(target), delay);
  }

  async function handleSubmit() {
    const errors: Record<string, string> = {};
    if (!EMAIL_RE.test(state.email.trim())) {
      errors.email = "Indtast en gyldig emailadresse";
    }
    if (!isValidDanishNational(state.phone)) {
      errors.phone = "Indtast et gyldigt dansk telefonnummer (8 cifre)";
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    if (inFlight.current) return;
    inFlight.current = true;
    setIsSubmitting(true);
    setSubmitError(null);
    const startedAt = Date.now();
    let redirectStarted = false;

    // taskType is also used in the posthog capture below; the rest of the
    // payload is built in buildLeadPayload.
    const taskType = state.audience === "privat" ? state.task : state.businessTask;

    try {
      const eventId = generateEventId("Lead");
      const res = await fetch("/api/lead", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-PostHog-Id": posthog?.get_distinct_id?.() || "unknown",
        },
        body: JSON.stringify(buildLeadPayload(state, utmParams, eventId, honeypot, isRepeatTask)),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => null);
        setSubmitError(body?.error || "Noget gik galt. Prøv igen.");
        setIsSubmitting(false);
        return;
      }

      const responseBody = await parseLeadResponse(res);

      // Every accepted submission owns a token page — the match always creates
      // one, even for NO_MATCH and for a task parked for review. So a
      // missing path means the submission did NOT land, and saying "thanks"
      // here would repeat the bug this replaced: a confirmation screen for a
      // lead that does not exist.
      const thankYouPath = taskPagePath(responseBody);
      if (!thankYouPath) {
        setSubmitError("Noget gik galt. Prøv igen.");
        return;
      }

      // Conversions fire only once the submission is known to have landed - // above the check they would report a Lead for a form the customer is
      // being told to retry, and the retry mints a fresh eventId Meta can't
      // dedupe against.
      posthog?.capture(
        state.audience === "erhverv" ? "erhverv_lead_submitted" : "privat_lead_submitted",
        {
          audience: state.audience,
          task_type: taskType,
          business: state.business,
          frequency: state.frequency,
          square_meters: state.squareMeters ? Number.parseInt(state.squareMeters, 10) : null,
          // Also on wizard_started — on both events so the thank-you CTA can be
          // measured as a funnel (opened → actually submitted), not just clicks.
          is_repeat_task: isRepeatTask,
        },
      );
      if (state.audience) {
        trackEvent("Lead", { content_category: state.audience }, eventId);
      }

      await holdMatchingOverlay(startedAt, () => setMatchFound(true));
      router.push(thankYouPath);
      redirectStarted = true;
    } catch {
      setSubmitError("Noget gik galt. Prøv igen.");
    } finally {
      if (!redirectStarted) {
        inFlight.current = false;
        setIsSubmitting(false);
      }
    }
  }

  const ActiveStep = activeStepId ? STEP_COMPONENTS[activeStepId] : null;

  return (
    <WizardProvider
      value={{
        state,
        setState,
        update,
        step,
        goToStep,
        autoAdvance,
        stepReady,
        isBusiness,
        fieldErrors,
        setFieldError,
        validateEmailField,
        validatePhoneField,
        toggle,
        handleAddressSelect,
        handleSkipAddress,
        handleResetAddress,
        bbrLoading,
        bbrError,
        honeypot,
        setHoneypot,
        submitError,
        isSubmitting,
        handleSubmit,
        postalMatch,
      }}
    >
      <div
        ref={rootRef}
        className={
          isHero
            ? "w-full"
            : "mx-auto grid max-w-page grid-cols-1 gap-14 md:grid-cols-[1fr_280px] md:gap-14"
        }
      >
        {isSubmitting ? <MatchingOverlay isBusiness={!isPrivat} complete={matchFound} /> : null}
        <div>
          <ProgressBar
            pct={wizardPct(step, totalSteps)}
            left={phaseLabelFor(activeStepId) ?? "Kom i gang"}
            right={`${wizardPct(step, totalSteps)}%`}
          />

          {isRepeatTask && step === 1 && (
            <PrefillNotice audience={initialAudience} postalCode={initial.postalCode ?? ""} />
          )}

          <WizardStepShell
            stepKey={step}
            direction={direction}
            stepsRef={stepsRef}
            lockStyle={lockStyle}
          >
            {ActiveStep ? <ActiveStep /> : null}
          </WizardStepShell>
        </div>

        {!isHero && <WizardSidebar />}
      </div>
    </WizardProvider>
  );
}
